import { Grid, Typography } from '@mui/material';
import { Cropper } from 'shell/components/shared/cropper';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useFormik, useFormikContext } from 'formik';
import {
  CertificationFormVM,
  DocumentBirthFormVM,
} from 'front-office/view-models/document-birth-form';
import { PageMode } from 'civil-status/view-models/page-mode';
import DescriptionForm from 'civil-status/components/shared/description-document';
import { StickyDiv } from 'shell/components/shared/sticky-div';
import DocumentBirthFormShared from 'civil-status/components/shared/birth-form';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { __CIVIL_STATUS_API_URL__ } from 'shell/constants/environment';
import {
  transformDocumentBirth,
  transformDocumentBirthRequestUpdate,
} from 'civil-status/utils/transform-document-birth';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { DocumentBirthCM } from 'shell/models/document-birth';
import createSchema from './create-schema-birth';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { toast } from 'react-toastify';
import { transformCertificateTitle } from 'front-office/utils/transform-certificate-title';
import AnnotationForm from 'civil-status/components/shared/annotation';
import DocumentVisualizationsButtonsForm from './buttons';
import { blobToBase64 } from 'shell/utils/base64-to-blob';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { noop } from 'shell/utils/noop';

interface DocumentVisualizationBirthProps {
  onBack: () => void;
  onNext: (values: DocumentBirthFormVM) => void;
  type: string;
}

const DocumentVisualizationBirthComponent = ({
  onBack,
  onNext,
  type,
}: DocumentVisualizationBirthProps): React.ReactElement => {
  const [visibilityWarning, setVisibilityWarning] = useState<boolean>(false);

  const formContext = useFormikContext<CertificationFormVM>();

  const { values: params } = formContext;

  const [birth] = useHttpFetch<HttpResponse<DocumentBirthCM>, DocumentBirthFormVM>(
    `${__CIVIL_STATUS_API_URL__}/atti-nascita-sima/visualizzazione-atto-nascita/${
      params.chosenDocument?.year
    }-${params.chosenDocument?.numReg}-${params.chosenDocument?.documentNumber}-${
      params.chosenDocument?.parte || 'null'
    }-${params.chosenDocument?.serie || 'null'}`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'GET',
        loading: true,
      },
      transformer: transformDocumentBirth,
    }
  );

  const updateBirth = useHttpClient(
    `${__CIVIL_STATUS_API_URL__}/atti-nascita-sima/modifica-atto-nascita`,
    {
      options: {
        method: 'PUT',
        loading: true,
        errorCatching: true,
      },
    }
  );

  const [mode, setMode] = useState<PageMode>(PageMode.Detail);

  const formikForm = useFormik<DocumentBirthFormVM>({
    initialValues: birth.data as DocumentBirthFormVM,
    enableReinitialize: true,
    validationSchema: createSchema,
    onSubmit: (values: DocumentBirthFormVM, context) => {
      // If edit mode
      if (mode === PageMode.Edit) {
        const data = transformDocumentBirthRequestUpdate(values);
        (
          updateBirth({
            data,
          }) as ResponsePromise<void>
        ).then(() => {
          toast.success('Operazione eseguita con successo');
          setMode(PageMode.Detail);
          context.setFieldValue('indicatorUpdate', '');
        });
        return;
      }
      onNext(values);
    },
  });

  const { values, errors, touched, setFieldValue: onChange } = formikForm;

  useEffect(() => {
    if (values?.indicatorUpdate) {
      setVisibilityWarning(true);
    } else {
      setVisibilityWarning(false);
    }
  }, [values?.indicatorUpdate]);

  const unlockDocument = useHttpClient(
    `${__CIVIL_STATUS_API_URL__}/atti-nascita-sima/sblocca-atto`,
    {
      options: {
        errorCatching: true,
        method: 'PUT',
        loading: true,
      },
    }
  );

  const onChanges = useCallback(
    (newValues) => {
      formikForm.setValues({
        ...formikForm.values,
        ...newValues,
      });
    },
    [formikForm.setValues, formikForm.values]
  );

  useEffect(() => {
    return () => {
      if (values) {
        unlockDocument({
          data: {
            key: {
              numeroAtto: values.documentNumber,
              numeroRegistro: values.registerNumber,
              parte: values.portion,
              serie1: `${values.serie || ''}${values.serie2 || ''}`,
              anno: values.year,
            },
          },
        });
      }
    };
  }, [
    values?.documentNumber,
    values?.registerNumber,
    values?.portion,
    values?.serie,
    values?.year,
  ]);

  const atto = useMemo(() => {
    return transformCertificateTitle(formContext.values?.certificate);
  }, [formContext.values?.certificate]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Typography
        color="secondary.main"
        style={{
          fontSize: '26px',
        }}
      >
        Visualizzazione Atto {atto.type}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <SearchMessages
            type={MessageType.Error}
            title={!formikForm.isValid ? `Sono presenti errori` : ''}
            messages={
              !formikForm.isValid
                ? [
                    {
                      id: 1,
                      value: "Entrare in modifica dell'atto e compilare i campi obbligatori",
                    },
                  ]
                : []
            }
            visibility={!formikForm.isValid}
            onChangeVisibility={noop}
          />
        </Grid>
        <Grid item xs={12}>
          <SearchMessages
            type={MessageType.Warning}
            title={formikForm.values?.indicatorUpdate ? `C'è 1 warning` : ''}
            messages={
              formikForm.values?.indicatorUpdate
                ? [{ id: 1, value: formikForm.values?.indicatorUpdate }]
                : []
            }
            visibility={visibilityWarning}
            onChangeVisibility={() => setVisibilityWarning(false)}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <DocumentVisualizationsButtonsForm
            values={values}
            type={type}
            handleSubmit={() => formikForm.handleSubmit()}
            mode={mode}
            onBack={onBack}
            onChange={onChange}
            setMode={setMode}
          />
          <DescriptionForm values={values} />
          {mode === PageMode.Detail || mode === PageMode.Edit ? (
            <DocumentBirthFormShared
              onChange={onChange}
              onChanges={onChanges}
              values={values}
              errors={errors}
              mode={mode}
              touched={touched}
            />
          ) : undefined}
          <AnnotationForm
            values={values}
            onChange={onChange}
            mode={mode}
            type={type}
            setMode={setMode}
          />
        </Grid>
        <Grid item xs={12} md={8}>
          <StickyDiv>
            <Cropper
              images={values?.uploadFile?.raw || []}
              disabled={!values?.uploadFile?.raw?.length}
              onCut={(blob, indexImage) => {
                if (!blob) {
                  return;
                }
                blobToBase64(blob).then((base64) => {
                  onChange('uploadFile', {
                    ...(values?.uploadFile || {}),
                    raw:
                      values?.uploadFile?.raw?.map((el, index) =>
                        index === indexImage ? base64 : el
                      ) || [],
                    rawTiff: base64,
                  });
                });
              }}
            />
          </StickyDiv>
        </Grid>
      </Grid>
    </>
  );
};

DocumentVisualizationBirthComponent.displayName = 'Document Birth form component';

const DocumentVisualizationBirthForm = memo(DocumentVisualizationBirthComponent);

export default DocumentVisualizationBirthForm;
